import { debounce, get } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import {
  FilterFieldOption,
  FilterFieldOptionsHookResult,
} from "~/features/filter/model/types";
import { Profile, useGetUsersLazyQuery } from "~/schemas/generated/graphql";

const PAGE_SIZE = 25;

/**
 * Опции фильтра «Пользователи»: постраничная подгрузка по скроллу
 * и поиск с debounce (GraphQL UserGetter.getUsers).
 */
export const useUserOptions = (): FilterFieldOptionsHookResult => {
  const [getUsers, result] = useGetUsersLazyQuery({
    variables: { page: 0, count: PAGE_SIZE },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const options = useMemo<FilterFieldOption[]>(
    () =>
      get(result, "data.UserGetter.getUsers.items", []).map(
        (item: Profile) => ({
          value: String(item.id),
          label: item.fullname ?? "",
        }),
      ),
    [result],
  );

  const onScrollBottom = useCallback(() => {
    const { fetchMore } = result;
    if (!fetchMore) return;

    fetchMore({
      variables: {
        page: Math.ceil(options.length / PAGE_SIZE),
      },
      updateQuery: ({ UserGetter }, { fetchMoreResult }) => {
        if (!fetchMoreResult) return { UserGetter };
        return {
          UserGetter: {
            ...UserGetter,
            getUsers: {
              ...UserGetter.getUsers,
              items: [
                ...UserGetter.getUsers.items,
                ...get(fetchMoreResult, "UserGetter.getUsers.items", []),
              ],
            },
          },
        };
      },
    });
  }, [result, options.length]);

  const searchOptions = useMemo(
    () =>
      debounce((searchText: string) => {
        getUsers({
          variables: {
            page: 0,
            count: PAGE_SIZE,
            searchText: searchText || null,
          },
        });
      }, 400),
    [getUsers],
  );

  return {
    options,
    loading: result.loading,
    onScrollBottom,
    searchOptions,
  };
};
